import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";
import { getNextPaginatePage } from "@/utils";

import { type AdminCategory } from "@/modules/news/admin/types";

export type CategoryList = Paginate<AdminCategory>;

export const categories = createQueryKeys("admin-news-categories", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  allInfinite: (filter?: SearchRequest) => [{ filter }],
  one: (id?: number) => [id],
});

const include = "";

export const fetchCategoriesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...categories.all(page, search),
    queryFn: () =>
      adminApi
        .get<CategoryList>(`/news/categories`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchCategories = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchCategoriesQueryOptions(page, search));

export const fetchInfiniteCategoriesQueryOptions = (search?: SearchRequest) =>
  infiniteQueryOptions({
    ...categories.allInfinite(search),
    queryFn: ({ pageParam }) =>
      adminApi
        .get<CategoryList>(`/news/categories`, {
          params: { page: pageParam, include, ...search },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchInfiniteCategories = (search?: SearchRequest) =>
  useInfiniteQuery({ ...fetchInfiniteCategoriesQueryOptions(search) });

export const fetchCategoryQueryOptions = (id?: number) =>
  queryOptions({
    ...categories.one(id),
    queryFn: () =>
      adminApi
        .get<AdminCategory>(`/news/categories/${id}?include=${include}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchCategory = (id?: number) =>
  useQuery(fetchCategoryQueryOptions(id));

export interface CategoryRequest {
  name: string;
  color: string;
}

export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: CategoryRequest) =>
      adminApi.post(`/news/categories`, data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: categories._def,
      });
    },
  });
};

export const useUpdateCategory = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: CategoryRequest) =>
      adminApi
        .put(`/news/categories/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: categories._def,
      });
    },
  });
};

export const useCreateOrUpdateCategory = (id: number | undefined) => {
  const create = useCreateCategory();
  const update = useUpdateCategory(id!);
  return id ? update : create;
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`news/categories/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: categories._def,
      });
      queryClient.invalidateQueries(categories.one(id));
    },
  });
};
